import { Link } from "react-router-dom";
import { useMemo } from "react";
import { allQuizzes } from "../../../utils/allQuizzes";

function formatPlays(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

const RANK_LABELS: Record<number, string> = {
  0: "#1 Hottest",
  1: "#2 Trending",
  2: "#3 Rising",
};

export default function FeaturedQuizzes() {
  const topQuizzes = useMemo(() => {
    return [...allQuizzes]
      .sort((a, b) => b.totalPlays - a.totalPlays)
      .slice(0, 8);
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-orange-50 text-[#FF6B6B] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            🔥 Trending This Week
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-3"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Most Popular Quizzes
          </h2>
          <p className="text-gray-500 text-base max-w-md mx-auto">
            Millions of teens can&apos;t stop taking these. Which one calls your name?
          </p>
        </div>

        {/* Quiz Grid — top 8 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {topQuizzes.map((quiz, i) => (
            <Link
              key={quiz.id}
              to={`/quiz/${quiz.id}`}
              className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#FF6B6B]/30 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
            >
              {/* Image */}
              <div className="w-full h-44 overflow-hidden relative">
                <img
                  src={quiz.image}
                  alt={quiz.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                {/* Top 3 rank badge */}
                {i < 3 && (
                  <div className="absolute top-3 left-3 bg-[#FF6B6B] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    {RANK_LABELS[i]}
                  </div>
                )}
                {/* Hot streak badge for 4-8 */}
                {i >= 3 && i < 8 && (
                  <div className="absolute top-3 left-3 bg-orange-400/90 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    🔥 Hot
                  </div>
                )}
                <div className="absolute top-3 right-3 bg-white/90 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full">
                  {quiz.estimatedTime}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <span className="text-xs font-bold text-[#FF6B6B] uppercase tracking-wider">
                  {quiz.categoryLabel}
                </span>
                <h3
                  className="font-extrabold text-gray-900 text-sm mt-1 mb-2 leading-snug line-clamp-2 group-hover:text-[#FF6B6B] transition-colors"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {quiz.title}
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <i className="ri-user-line" />
                    <span>{formatPlays(quiz.totalPlays)} plays</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-amber-500">
                    <i className="ri-star-fill" />
                    <span>{quiz.rating}</span>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 text-[#FF6B6B] text-sm font-bold group-hover:gap-3 transition-all">
                  <span>Start Quiz</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#FF6B6B] text-[#FF6B6B] font-bold rounded-full hover:bg-[#FF6B6B] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            See All Quizzes
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
